import React, { useState } from 'react';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import { useCurrency } from '../../contexts/CurrencyContext';

const Overlay = styled.div`
  position: fixed;
  z-index: 1000;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 92%;
  max-width: 860px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #C4845C;
    border-radius: 4px;
  }
`;

const ModalHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #333;
  }
`;

const Content = styled.div`
  padding: 28px;
`;

const Section = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 16px 0;
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 28px;
  row-gap: 16px;
  grid-auto-rows: minmax(32px, auto);
  margin-bottom: 24px;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const CategoryOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  line-height: 1.35;
  padding: 2px 0;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  flex-shrink: 0;
  border: 2px solid #C4845C;
  border-radius: 4px;
  cursor: pointer;
  accent-color: #C4845C;
  box-sizing: border-box;

  &:checked {
    background-color: #C4845C;
    border-color: #C4845C;
  }
`;

const NumberOptionsGrid = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

const NumberOption = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.selected ? '#C4845C' : '#e5e5e5'};
  background: ${props => props.selected ? '#C4845C' : 'white'};
  color: ${props => props.selected ? 'white' : '#333'};
  border-radius: 100px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #C4845C;
  }
`;

const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  bottom: 0;
  background: white;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  padding: 8px 16px;

  &:hover {
    color: #333;
  }
`;

const ApplyButton = styled.button`
  background: #C4845C;
  color: white;
  border: none;
  border-radius: 100px;
  padding: 8px 24px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #b37346;
  }
`;

const AreaSection = styled.div`
  margin-bottom: 24px;
`;

const AreaInputs = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
`;

const CurrencyTag = styled.span`
  padding: 8px 10px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  font-weight: 700;
  font-size: 13px;
  color: #2d3748;
  background: #f8fafc;
`;

const AreaInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  width: 120px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #C4845C;
  }

  &::placeholder {
    color: #999;
  }
`;

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 4px;
  background: #e5e5e5;
  border-radius: 2px;
  margin: 20px 0;
`;

const SliderTrack = styled.div`
  position: absolute;
  height: 100%;
  background: #C4845C;
  border-radius: 2px;
  left: ${props => (props.$minValue / props.max) * 100}%;
  right: ${props => 100 - (props.$maxValue / props.max) * 100}%;
`;

const SliderInput = styled.input`
  position: absolute;
  width: 100%;
  height: 24px;
  opacity: 0;
  pointer-events: auto;

  &::-webkit-slider-thumb {
    pointer-events: all;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    cursor: pointer;
    -webkit-appearance: none;
  }
`;

const SliderThumbLeft = styled.div`
  position: absolute;
  left: ${props => (props.value / props.max) * 100}%;
  width: 18px;
  height: 18px;
  background: white;
  border: 2px solid #C4845C;
  border-radius: 50%;
  transform: translate(-50%, -7px);
  pointer-events: none;
`;

const SliderThumbRight = styled.div`
  position: absolute;
  left: ${props => (props.value / props.max) * 100}%;
  width: 18px;
  height: 18px;
  background: white;
  border: 2px solid #C4845C;
  border-radius: 50%;
  transform: translate(-50%, -7px);
  pointer-events: none;
`;

const ModalFilters = ({
  isOpen,
  onClose,
  selectedTypes,
  setSelectedTypes,
  // label atualmente selecionado no dropdown principal (ex.: "Casas de praia")
  selectedLabel,
  // opções dinâmicas para Tipo/Categoria (cada item: { id, label, kind })
  typeOptions = [],
  typeOptionsTitle = 'Tipo de imóvel',
  quartos,
  setQuartos,
  banheiros,
  setBanheiros,
  garagem,
  setGaragem,
  minArea,
  setMinArea,
  maxArea,
  setMaxArea,
  onClear,
  onApply,
  // opcionais: controlar preço também pelo modal
  minPrice,
  setMinPrice,
  maxPrice,
  setMaxPrice,
  // opcional: controlar situação do imóvel externamente
  situacaoValue,
  onSituacaoChange,
  // Comodidades booleanas (0/1)
  amenities = {},
  setAmenities,
  // tipo de busca atual: buy | rent | vacation/ferias
  searchType = 'buy',
}) => {
  if (!isOpen) return null;

  const { currency } = useCurrency();

  const numberOptions = ['1+', '2+', '3+', '4+', '5+'];

  // Tipologia: mapeia para quantidade de quartos
  const tipologias = [
    { label: 'T0', value: 0 },
    { label: 'T1', value: 1 },
    { label: 'T2', value: 2 },
    { label: 'T3', value: 3 },
    { label: 'T4+', value: 4 },
  ];

  // Situação do imóvel (controlado externamente se props fornecidas)
  const [internalSituacao, setInternalSituacao] = useState('');
  const situacao = situacaoValue ?? internalSituacao;
  const setSituacao = (val) => {
    if (typeof onSituacaoChange === 'function') onSituacaoChange(val);
    else setInternalSituacao(val);
  };

  const toggleAmenity = (key) => {
    if (typeof setAmenities === 'function') {
      setAmenities(prev => ({ ...prev, [key]: !prev?.[key] }));
    }
  };

  const amenitiesList = [
    { key: 'financiamento', label: 'Financiamento' },
    { key: 'disponivel', label: 'Disponível' },
    { key: 'tv', label: 'TV' },
    { key: 'aquecimento', label: 'Aquecimento' },
    { key: 'cozinha', label: 'Cozinha' },
    { key: 'maquinadelavar', label: 'Máquina de lavar' },
    { key: 'estacionamento', label: 'Estacionamento' },
    { key: 'piscina', label: 'Piscina' },
    { key: 'varanda', label: 'Varanda' },
    { key: 'churrasqueira', label: 'Churrasqueira' },
    { key: 'casadebanho', label: 'Casas de banho extra' },
    { key: 'seguranca24h', label: 'Segurança 24h' },
    { key: 'mobiliado', label: 'Mobiliado' },
    { key: 'espacoprivado', label: 'Espaço privado' },
    { key: 'adega', label: 'Adega' },
    { key: 'sprinklers', label: 'Sprinklers' },
    { key: 'pets', label: 'Pets' },
    { key: 'sauna', label: 'Sauna' },
    { key: 'festa', label: 'Festas' },
    { key: 'academia', label: 'Academia' },
    { key: 'portaria', label: 'Portaria' },
    { key: 'playground', label: 'Playground' },
    { key: 'proximoaometro', label: 'Próximo ao metrô' },
    { key: 'elevador', label: 'Elevador' },
    { key: 'porao', label: 'Porão' },
    { key: 'jardimdefrente', label: 'Jardim de frente' },
    { key: 'vistadolago', label: 'Vista do lago' },
    { key: 'vistadomar', label: 'Vista do mar' },
    { key: 'wifi', label: 'Wi‑Fi' },
    { key: 'closet', label: 'Closet' },
    { key: 'arcondicionado', label: 'Ar-condicionado' },
  ];

  const handleSliderChange = (e, type) => {
    const value = Number(e.target.value);
    if (type === 'min') {
      setMinArea(Math.min(value, maxArea - 1));
    } else {
      setMaxArea(Math.max(value, minArea + 1));
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <Title>Filtros</Title>
          <CloseButton onClick={onClose}>
            <IoClose size={24} />
          </CloseButton>
        </ModalHeader>

        <Content>
          {/* Tipo/Categoria (multi-seleção) */}
          {!!typeOptions.length && (
            <Section>
              <SectionTitle>{typeOptionsTitle}</SectionTitle>
              <CategoryGrid>
                {typeOptions.map((opt) => {
                  const normalize = (str) => String(str || '')
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/[-_]+/g, ' ')
                    .toLowerCase()
                    .trim();

                  const checkedById = selectedTypes?.some((s) => String(s.id) === String(opt.id) && s.kind === opt.kind);
                  const checkedByLabel = selectedLabel
                    ? normalize(selectedLabel) === normalize(opt.label || '')
                    : false;
                  const checked = checkedById || checkedByLabel;
                  return (
                    <CategoryOption key={`${opt.kind}-${opt.id}`}>
                      <Checkbox
                        type="checkbox"
                        checked={!!checked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTypes([...(selectedTypes || []), { id: opt.id, kind: opt.kind }]);
                          } else {
                            setSelectedTypes((selectedTypes || []).filter((s) => !(String(s.id) === String(opt.id) && s.kind === opt.kind)));
                          }
                        }}
                      />
                      {opt.label}
                    </CategoryOption>
                  );
                })}
              </CategoryGrid>
            </Section>
          )}

          {/* Tipologia removida (não suportada na API). Manter apenas filtros compatíveis. */}

          {/* Preço - usa os setters se fornecidos por props */}
          {(typeof setMinPrice === 'function' && typeof setMaxPrice === 'function') && (
            <Section>
              <SectionTitle>Preço</SectionTitle>
              <AreaInputs>
                <CurrencyTag key={currency}>{currency || 'EUR'}</CurrencyTag>
                <AreaInput
                  type="number"
                  placeholder="Min"
                  value={minPrice ?? ''}
                  onChange={(e) => setMinPrice?.(e.target.value)}
                />
                <span style={{ fontWeight: 600 }}>→</span>
                <CurrencyTag key={currency}>{currency || 'EUR'}</CurrencyTag>
                <AreaInput
                  type="number"
                  placeholder="Max"
                  value={maxPrice ?? ''}
                  onChange={(e) => setMaxPrice?.(e.target.value)}
                />
              </AreaInputs>
            </Section>
          )}

          {/* Situação do Imóvel (numérico 1/2/3) */}
          {searchType !== 'vacation' && searchType !== 'ferias' && (
            <Section>
              <SectionTitle>Situação do Imóvel</SectionTitle>
              <CategoryGrid>
                {[
                  { label: 'Novo', value: 1 },
                  { label: 'Usado', value: 2 },
                  { label: 'Em Construção', value: 3 },
                ].map((opt) => (
                  <CategoryOption key={opt.value}>
                    <Checkbox
                      type="radio"
                      name="situacao-imovel"
                      checked={Number(situacao) === Number(opt.value)}
                      onChange={() => setSituacao(opt.value)}
                    />
                    {opt.label}
                  </CategoryOption>
                ))}
              </CategoryGrid>
            </Section>
          )}

          {/* Comodidades */}
          {typeof setAmenities === 'function' && (
            <Section>
              <SectionTitle>Comodidades</SectionTitle>
              <CategoryGrid>
                {amenitiesList.map((a) => (
                  <CategoryOption key={a.key}>
                    <Checkbox
                      type="checkbox"
                      checked={!!amenities?.[a.key]}
                      onChange={() => toggleAmenity(a.key)}
                    />
                    {a.label}
                  </CategoryOption>
                ))}
              </CategoryGrid>
            </Section>
          )}

          <Section>
            <SectionTitle>Quartos</SectionTitle>
            <NumberOptionsGrid>
              {numberOptions.map((num) => (
                <NumberOption
                  key={num}
                  selected={quartos === parseInt(num)}
                  onClick={() => setQuartos(parseInt(num))}
                >
                  {num}
                </NumberOption>
              ))}
            </NumberOptionsGrid>
          </Section>

          <Section>
            <SectionTitle>Casas de banho</SectionTitle>
            <NumberOptionsGrid>
              {numberOptions.map((num) => (
                <NumberOption
                  key={`bath-${num}`}
                  onClick={() => setBanheiros(num)}
                  $selected={banheiros === num}
                >
                  {num}+
                </NumberOption>
              ))}
            </NumberOptionsGrid>
          </Section>

          <Section>
            <SectionTitle>Garagem</SectionTitle>
            <NumberOptionsGrid>
              {numberOptions.map((num) => (
                <NumberOption
                  key={num}
                  selected={garagem === parseInt(num)}
                  onClick={() => setGaragem(parseInt(num))}
                >
                  {num}
                </NumberOption>
              ))}
            </NumberOptionsGrid>
          </Section>

          <Section>
            <SectionTitle>Metragem</SectionTitle>
            <SliderContainer>
              <SliderTrack $minValue={minArea} $maxValue={maxArea} max={1000} />
              <SliderInput
                type="range"
                min={0}
                max={1000}
                value={minArea}
                onChange={(e) => handleSliderChange(e, 'min')}
              />
              <SliderInput
                type="range"
                min={0}
                max={1000}
                value={maxArea}
                onChange={(e) => handleSliderChange(e, 'max')}
              />
              <SliderThumbLeft value={minArea} max={1000} />
              <SliderThumbRight value={maxArea} max={1000} />
            </SliderContainer>
            <p>
              {minArea}m² - {maxArea}m²
            </p>
          </Section>
        </Content>

        <Footer>
          <ClearButton onClick={() => {
            onClear();
            setMinArea(0);
            setMaxArea(1000);
            setSelectedTypes?.([]);
          }}>
            Remover filtros
          </ClearButton>
          <ApplyButton onClick={onApply}>
            Aplicar
          </ApplyButton>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default ModalFilters;